import { z } from "zod";

/**
 * Client-side environment variables (VITE_* variables)
 * These are available in both server and client contexts
 */
const clientEnvSchema = z.object({
  VITE_BUILDER_API_KEY: z.string().min(1, "VITE_BUILDER_API_KEY is required"),
  VITE_CLERK_PUBLISHABLE_KEY: z
    .string()
    .min(1, "VITE_CLERK_PUBLISHABLE_KEY is required"),
  VITE_APP_HOST: z.string().optional(),
  VITE_APP_URL: z.string().optional(),
  VITE_GHL_COMPANY_ID: z.string().optional(),
  VITE_GHL_LOCATION_ID: z.string().optional(),
  VITE_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
  VITE_GHL_COACH_APPLICATION_FORM_URL: z.string().url().optional(),
});

/**
 * Parse and validate client environment variables
 * Uses import.meta.env for client-side access
 */
function createClientEnv(): ClientEnv {
  const result = clientEnvSchema.safeParse({
    ...process.env,
    ...import.meta.env,
  });

  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
      .join("\n");

    throw new Error(`Invalid client environment variables:\n${issues}`);
  }

  return result.data;
}

let _clientEnv: ClientEnv | null = null;

/**
 * Client-side environment variables — lazy proxy so importing this module
 * doesn't blow up in contexts where VITE_* vars aren't set (e.g. unit tests).
 * Validation runs on first property access.
 */
export const clientEnv: ClientEnv = new Proxy({} as ClientEnv, {
  get(_target, prop: string) {
    if (!_clientEnv) {
      _clientEnv = createClientEnv();
    }
    return _clientEnv[prop as keyof ClientEnv];
  },
});

export type ClientEnv = z.infer<typeof clientEnvSchema>;
